import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';
import API_BASE from '../utils/apiBase';

const API = API_BASE;
const OrgContext = createContext(null);

// The platform never impersonates Osabea (or any other tenant) before the
// authenticated workspace has supplied its own identity.
const UNRESOLVED_ORGANISATION = Object.freeze({
  organisation_name: '',
  company_name: '',
  service_type: null,
  logo_url: null,
});

function resolvedTenantSettings(data, expectedWorkspaceId) {
  if (!data || typeof data !== 'object' || Array.isArray(data)) {
    throw new Error('Organisation settings are not available');
  }
  // Active-workspace claims are a security boundary. Reject a server response
  // that points at a different tenant, rather than displaying its branding.
  if (
    expectedWorkspaceId &&
    String(data.workspace_id || '') !== String(expectedWorkspaceId)
  ) {
    throw new Error('Organisation settings do not match the active workspace');
  }
  return { ...UNRESOLVED_ORGANISATION, ...data };
}

export function OrgProvider({ children }) {
  const { token, user } = useAuth();
  const workspaceId = user?.workspace_id || user?.active_workspace_id || '';
  const sessionKey = token && user
    ? [token, user?.user_id || user?.id || '', workspaceId].join(':')
    : '';
  const shouldFetch = Boolean(sessionKey) && user?.role !== 'platform_owner';
  const [resolved, setResolved] = useState({ sessionKey: '', settings: null, loading: false });
  // Changing accounts/workspaces must hide the previous tenant immediately,
  // even before the effect cleanup or the next network response executes.
  const matchesSession = shouldFetch && resolved.sessionKey === sessionKey;
  const orgSettings = matchesSession
    ? { ...(resolved.settings || UNRESOLVED_ORGANISATION), loading: resolved.loading }
    : { ...UNRESOLVED_ORGANISATION, loading: Boolean(token && user && shouldFetch) };

  useEffect(() => {
    if (!shouldFetch) {
      setResolved({ sessionKey: '', settings: null, loading: false });
      return undefined;
    }

    const controller = new AbortController();
    let active = true;
    setResolved({ sessionKey, settings: null, loading: true });

    axios.get(`${API}/org-settings`, {
      headers: { Authorization: `Bearer ${token}` },
      signal: controller.signal,
    }).then((response) => {
      const settings = resolvedTenantSettings(response.data, workspaceId);
      if (active) setResolved({ sessionKey, settings, loading: false });
    }).catch((error) => {
      if (active) {
        // Never show previously resolved or first-tenant branding on errors.
        console.error('Failed to load organisation settings:', error);
        setResolved({ sessionKey, settings: null, loading: false });
      }
    });

    return () => {
      active = false;
      controller.abort();
    };
  }, [token, sessionKey, workspaceId, shouldFetch]);

  const updateOrgSettings = async (updates) => {
    if (!shouldFetch) {
      return { success: false, error: 'A tenant workspace session is required' };
    }
    try {
      const response = await axios.put(
        `${API}/org-settings`,
        updates,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const settings = resolvedTenantSettings(response.data, workspaceId);
      // The write belongs only to the session that issued it; stale updates
      // cannot stamp an unrelated tenant's branding into the visible UI.
      setResolved((current) => current.sessionKey === sessionKey
        ? { sessionKey, settings, loading: false }
        : current);
      return { success: true, data: settings };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to update settings',
      };
    }
  };

  return (
    <OrgContext.Provider value={{
      orgSettings,
      orgName: orgSettings.organisation_name || orgSettings.company_name || 'Your organisation',
      orgLogoUrl: orgSettings.logo_url || null,
      orgPrimaryColor: orgSettings.primary_color || '#0f766e',
      serviceType: orgSettings.service_type,
      updateOrgSettings,
      isLoading: orgSettings.loading,
      tenantBrandReady: Boolean(matchesSession && resolved.settings?.organisation_name),
    }}>
      {children}
    </OrgContext.Provider>
  );
}

export function useOrg() {
  const context = useContext(OrgContext);
  if (!context) {
    // Public/unwrapped screens do not assume the identity of the first tenant.
    return {
      orgSettings: { ...UNRESOLVED_ORGANISATION, loading: false },
      orgName: 'Your organisation',
      orgLogoUrl: null,
      orgPrimaryColor: '#0f766e',
      serviceType: null,
      updateOrgSettings: () => ({ success: false, error: 'No tenant session' }),
      isLoading: false,
      tenantBrandReady: false,
    };
  }
  return context;
}

export default OrgContext;
